import { CompanyApi } from './companyApi';
import { Api } from './index';
import {
  DASHBOARD_FEATURES,
  FEATURE_STATUS,
  PLANE,
  getFeaturesByPlane
} from '../utils/dashboardFeatures';

/**
 * Data layer for the rebuilt B2B dashboard.
 *
 * Phase one deliberately keeps every network call behind these two functions
 * so that when the aggregate endpoints land
 * (`/dashboard/summary` and `/dashboard/feature/:key`) the swap is local:
 * delete the per-feature adapters below, call the one endpoint, keep the
 * same return shape.
 */

const ok = (res) => res?.data?.meta?.code === 1;
const payload = (res) => res?.data?.data;

/* ------------------------------------------------------------------ *
 * People band
 * ------------------------------------------------------------------ */

/**
 * Everything the dashboard renders, in one request.
 *
 * `GET company/v1/dashboard/summary` consolidates what used to be six calls
 * (users-statistics, getEmployeeActivity, getRecentJoinedEmployees,
 * getTrendingContent, and the company record twice — once for the People band
 * and once for the feature toggles). It is scoped by the token and takes no
 * companyId, so it is only correct for the company you are logged into; the
 * super admin's View Company tab must keep the legacy path.
 */
export const fetchDashboard = async (companyId, { embedded = false } = {}) => {
  if (!embedded) {
    const summary = await CompanyApi.getB2BDashboardSummary().catch(() => null);

    if (ok(summary)) {
      const data = payload(summary) || {};
      return {
        people: {
          seatsBought: data.seatsBought ?? null,
          emptySeats: data.emptySeats ?? null,
          totalUsers: data.totalUsers ?? null,
          activeUsers: data.activeUsers ?? null,
          pendingUsers: data.pendingUsers ?? null,
          activeToday: data.activeToday ?? null,
          activeThisMonth: data.activeThisMonth ?? null,
          activeThisYear: data.activeThisYear ?? null,
          todayChangePercentage: data.todayChangePercentage ?? null,
          monthChangePercentage: data.monthChangePercentage ?? null,
          yearChangePercentage: data.yearChangePercentage ?? null,
          recentJoiners: Array.isArray(data.recentJoiners) ? data.recentJoiners : [],
          trendingContent: []
        },
        featureStats: buildFeatureStats(data.companyFlags, null)
      };
    }
    // Fall through to the legacy calls so an outage on the new endpoint
    // degrades to the old numbers rather than an empty dashboard.
  }

  const [people, featureStats] = await Promise.all([
    fetchPeopleStatsLegacy(companyId),
    fetchFeatureStatsLegacy(companyId)
  ]);

  return { people, featureStats };
};

/**
 * NOTE ON SCOPE: `getUserStatistics()` is scoped by the JWT and takes no
 * companyId, so it only reflects the logged-in company. `getEmployeeActivity`
 * and `getRecentJoinedEmployees` both accept a companyId and therefore work
 * for the super-admin View Company tab too. Where the two disagree, the
 * companyId-aware source wins.
 */
const fetchPeopleStatsLegacy = async (companyId) => {
  const results = await Promise.allSettled([
    CompanyApi.getEmployeeActivity(companyId),
    CompanyApi.getRecentJoinedEmployees(companyId),
    CompanyApi.getUserStatistics(),
    CompanyApi.getTrendingContent(companyId),
    companyId ? Api.getCompanyData(companyId) : Promise.resolve(null)
  ]);

  const [activityRes, joinersRes, statsRes, trendingRes, companyRes] = results.map((r) =>
    r.status === 'fulfilled' ? r.value : null
  );

  const activity = ok(activityRes) ? payload(activityRes) : {};
  const joiners = ok(joinersRes) ? payload(joinersRes) : [];
  const stats = ok(statsRes) ? payload(statsRes) : {};
  const trending = ok(trendingRes) ? payload(trendingRes) : [];
  const company = ok(companyRes) ? payload(companyRes) : {};

  // Seats bought is the licence count; empty seats is whatever isn't taken up
  // by a user record. Guarded so a missing seat count shows "—" not a negative.
  const seatsBought = Number(company?.no_of_seat_bought);
  const totalUsers = stats?.totalUsers ?? null;
  const emptySeats =
    Number.isFinite(seatsBought) && Number.isFinite(totalUsers)
      ? Math.max(0, seatsBought - totalUsers)
      : null;

  return {
    seatsBought: Number.isFinite(seatsBought) ? seatsBought : null,
    emptySeats,
    totalUsers: stats?.totalUsers ?? null,
    activeUsers: stats?.activeUsers ?? null,
    pendingUsers: stats?.pendingUsers ?? null,
    activeToday: activity?.todayUsers ?? null,
    activeThisMonth: activity?.monthUsers ?? null,
    activeThisYear: activity?.yearUsers ?? null,
    todayChangePercentage: activity?.todayChangePercentage ?? null,
    monthChangePercentage: activity?.monthChangePercentage ?? null,
    yearChangePercentage: activity?.yearChangePercentage ?? null,
    recentJoiners: Array.isArray(joiners) ? joiners : [],
    trendingContent: normaliseTrending(trending)
  };
};

/**
 * Trending content feeds the "Most used content" list. The endpoint's item
 * shape isn't pinned down, so pull the first field that looks like a title and
 * the first that looks like a count, rather than assuming one schema.
 */
const normaliseTrending = (raw) => {
  const list = Array.isArray(raw) ? raw : raw?.content || raw?.data || [];
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (typeof item === 'string') return { title: item, count: null };
      return {
        title: item?.title || item?.name || item?.contentName || item?.displayName || null,
        count: item?.count ?? item?.total ?? item?.usageCount ?? item?.plays ?? null
      };
    })
    .filter((item) => item.title)
    .slice(0, 5);
};

/* ------------------------------------------------------------------ *
 * Feature status
 * ------------------------------------------------------------------ */

/**
 * Resolves the four card states. Today only Shuru and PEAP carry a real
 * enable/disable flag on the company record (see EnableFeatures.jsx) — every
 * other feature reports `on`, which matches current product behaviour where
 * a company either has a feature or the page simply shows nothing.
 *
 * `flags` is the subset of the company record holding those toggles: the
 * `companyFlags` object from the summary endpoint, or the whole company
 * record on the legacy path — both are keyed by the same field names.
 */
const resolveStatuses = (flags) => {
  const statuses = {};

  DASHBOARD_FEATURES.forEach((feature) => {
    if (feature.toggle) {
      const enabled = flags?.[feature.toggle];
      statuses[feature.key] = enabled === false ? FEATURE_STATUS.OFF : FEATURE_STATUS.ON;
    } else {
      statuses[feature.key] = FEATURE_STATUS.ON;
    }
  });

  return statuses;
};

/** Per-feature card state, from the toggle flags plus whatever metrics exist. */
const buildFeatureStats = (flags, metrics) => {
  const statuses = resolveStatuses(flags);
  const stats = {};

  DASHBOARD_FEATURES.forEach((feature) => {
    stats[feature.key] = {
      status: statuses[feature.key],
      value: metrics?.[feature.key]?.value ?? null,
      adoption: metrics?.[feature.key]?.adoption ?? null,
      trend: metrics?.[feature.key]?.trend ?? null
    };
  });

  return stats;
};

/* ------------------------------------------------------------------ *
 * Feature metrics
 * ------------------------------------------------------------------ */

/**
 * PHASE ONE STATUS — READ THIS BEFORE ADDING NUMBERS.
 *
 * Every feature below renders its card, its status and its navigation today.
 * The headline metric is intentionally null until each endpoint's response
 * shape is confirmed against a real payload, rather than guessed here.
 *
 * To wire one up, add a case returning `{ value, adoption, trend }`:
 *
 *   badges        → CompanyApi.getCompanyBadgeStats()
 *   marketplace   → getMarketplaceDashboard()        (api/offeringApi)
 *   perks         → getCustomOfferings({ companyId }) (api/offeringApi)
 *   habit_tracker → Api.getHabitsHubCompanyDetail(companyId)
 *   shoutouts     → CompanyApi.getAllShoutouts()
 *   peap          → CompanyApi.getWellbankStats?.()
 *
 * `adoption` is a 0–1 share of active users and is the one field that must be
 * comparable across features — it is what makes the grid scannable.
 */
const fetchFeatureMetrics = async () => ({});

/* ------------------------------------------------------------------ */

const fetchFeatureStatsLegacy = async (companyId) => {
  const [companyRes, metrics] = await Promise.all([
    companyId ? Api.getCompanyData(companyId).catch(() => null) : Promise.resolve(null),
    fetchFeatureMetrics(companyId)
  ]);

  return buildFeatureStats(ok(companyRes) ? payload(companyRes) : null, metrics);
};

/** Count of features in a plane that need the admin's attention. */
export const countNeedingSetup = (stats, plane) =>
  getFeaturesByPlane(plane).filter(
    (feature) => stats?.[feature.key]?.status === FEATURE_STATUS.NEEDS_SETUP
  ).length;

/** Count of enabled features in a plane, for the band's right-hand summary. */
export const countEnabled = (stats, plane) => {
  const features = getFeaturesByPlane(plane);
  const enabled = features.filter(
    (feature) => stats?.[feature.key]?.status === FEATURE_STATUS.ON
  ).length;
  return { enabled, total: features.length };
};

export { PLANE };
